// ActiveUploads.tsx
import React, { useEffect, useState } from 'react'
import { useUploadStore } from 'hooks/useUploadStore'
import { IconChevronUp, IconUpload } from '@tabler/icons-react'
import { toast } from 'react-hot-toast'

export function ActiveUploads() {
  const { uploads, removeUpload, addUploads } = useUploadStore()
  const [isCollapsed, setIsCollapsed] = useState(false)

  const [errorToastDisplayed, setErrorToastDisplayed] = useState<{
    [key: string]: boolean
  }>({})

  useEffect(() => {
    uploads
      .filter((upload) => upload.status === 'error')
      .forEach((upload) => {
        if (!errorToastDisplayed[upload.id]) {
          toast.error(upload.error || 'An error occurred during upload.')
          setErrorToastDisplayed((prev) => ({ ...prev, [upload.id]: true }))
          removeUpload(upload.id)
        }
      })
  }, [uploads, errorToastDisplayed, removeUpload])

  const activeUploads = uploads.filter(
    (upload) =>
      upload.status === 'transcribing' ||
      upload.status === 'uploading' ||
      upload.status === 'pending' ||
      upload.status === 'error'
  )

  // useEffect(() => {
  //   addUploads([
  //     {
  //       id: '1',
  //       status: 'uploading',
  //       name: 'Making a video',
  //       progress: 25,
  //       type: 'youtube',
  //     },
  //     {
  //       id: '2',
  //       status: 'transcribing',
  //       name: 'How to win friends and influence people',
  //       progress: 25,
  //       type: 'youtube',
  //     },
  //   ])
  // }, [])

  if (activeUploads.length === 0) return null

  // Collapsed view - just show an icon
  if (isCollapsed) {
    return (
      <div
        className="fixed bottom-0 right-0 z-[1000] m-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-blue-100 bg-white/90 text-blue-500 shadow-lg transition-all duration-300 hover:shadow-xl"
        onClick={() => setIsCollapsed(false)}
        title="Active Uploads"
        style={{
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(240,249,255,0.85))',
        }}
      >
        <IconUpload size={18} />
        {activeUploads.length > 0 && (
          <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white">
            {activeUploads.length}
          </div>
        )}
      </div>
    )
  }

  // Expanded view with subtle glossy background
  return (
    <div
      className="fixed bottom-0 right-0 z-[1000] m-4 max-h-[80vh] w-64 overflow-hidden rounded-lg border border-blue-100 shadow-lg"
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        className="p-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(240,249,255,0.85))',
        }}
      >
        <div className="flex items-center justify-between text-slate-600">
          <h3 className="text-xs font-medium">Active Uploads</h3>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hover:text-blue-700 focus:outline-none"
            aria-label="Collapse"
          >
            <IconChevronUp size={16} />
          </button>
        </div>

        <div className="mt-2 max-h-[60vh] divide-y divide-blue-50 overflow-y-auto rounded-md p-2 backdrop-blur-sm">
          {activeUploads.map((upload) => (
            <div key={upload.id} className="py-2">
              <div className="flex items-end justify-between">
                <div className="flex flex-1 flex-col gap-1">
                  <h4 className="max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-bold text-gray-700">
                    {upload.name ||
                      upload.file?.name ||
                      (upload.type === 'youtube'
                        ? 'Retrieving YouTube video...'
                        : 'Retrieving file...')}
                  </h4>
                  {upload.status === 'uploading' && (
                    <>
                      <p className="w-fit animate-pulse text-[10px] text-gray-600">
                        Uploading...
                        {upload.progress > 0
                          ? ` (${upload.progress.toFixed(0)}%)`
                          : null}
                      </p>
                      {upload.progress > 0 && (
                        <div className="mt-1">
                          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-1.5 rounded-full bg-gradient-to-r from-toasty-blue-300 to-toasty-blue-600"
                              style={{ width: `${upload.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {upload.status === 'transcribing' && (
                    <p className="w-fit animate-pulse text-[10px] text-gray-600">
                      Transcribing...
                    </p>
                  )}
                  {upload.status === 'error' && (
                    <p className="text-[10px] text-gray-600">Error</p>
                  )}

                  {upload.status === 'uploading' && (
                    <button
                      onClick={() => {
                        removeUpload(upload.id)
                      }}
                      className="mt-1 w-fit text-[10px] text-gray-400 hover:text-red-500"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div className="min-w-16">
                  {/* Cancel button moved to the previous div */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
